import { useState } from 'react'

import { Menu, Text, Tooltip } from '@mantine/core'
import { DateInput } from '@mantine/dates'
import { ChevronDown, CircleAlert, Pencil, TriangleAlert } from 'lucide-react'
import dayjs from 'dayjs'

import StyledBadge from 'src/components/DataDisplay/StyledBadge/StyledBadge'
import DateShortcutButton from 'src/components/Inputs/DateShortcutButton/DateShortcutButton'
import StyledMenu from 'src/components/Overlays/StyledMenu/StyledMenu'
import { DocumentCategoryLabel } from 'src/graphql/types/documents'
import {
  ExpirationTrackerStatus,
  ExpirationTrackerStatusColor,
} from 'src/graphql/types/expirationTrackers'
import { EXPIRATION_WINDOW_DAYS } from 'src/lib/expirationTracking'
import { formatSnakeValue } from 'src/lib/formatters'

import DetailRow from './DetailRow'
import SectionHeader from './SectionHeader'

const STATUSES = Object.values(ExpirationTrackerStatus)

interface OverviewSectionProps {
  tracker: any
  onUpdateExpirationDate?: (newDate: Date) => void
  onStatusChange: (newStatus: string) => void
  isUpdatingStatus: boolean
  onOpenApplicant?: (applicantId: string, metaKey?: boolean) => void
}

const OverviewSection = ({
  tracker,
  onUpdateExpirationDate,
  onStatusChange,
  isUpdatingStatus,
  onOpenApplicant,
}: OverviewSectionProps) => {
  const [editingDate, setEditingDate] = useState(false)
  const [editDate, setEditDate] = useState<Date | null>(null)

  const daysRemaining = tracker.daysRemaining
  const daysLabel =
    daysRemaining < 0
      ? `${Math.abs(daysRemaining)} ${Math.abs(daysRemaining) === 1 ? 'day' : 'days'} overdue`
      : daysRemaining === 0
        ? 'Today'
        : `${daysRemaining} ${daysRemaining === 1 ? 'day' : 'days'}`

  const handleStartEdit = () => {
    setEditDate(new Date(tracker.expirationDate))
    setEditingDate(true)
  }

  const handleSaveDate = () => {
    if (editDate) {
      onUpdateExpirationDate(editDate)
      setEditingDate(false)
    }
  }

  const isOverdue = daysRemaining < 0
  const isExpiring = !isOverdue && daysRemaining <= EXPIRATION_WINDOW_DAYS

  const expirationTooltip = isOverdue
    ? `This document is ${Math.abs(daysRemaining)} day(s) overdue`
    : isExpiring
      ? `This document expires in ${daysRemaining} day(s)`
      : null

  const alertIconClass = isOverdue
    ? 'stroke-doubleNickel-error-500'
    : 'stroke-doubleNickel-warning-600'

  const isStatusDisabled = (status: ExpirationTrackerStatus) =>
    isUpdatingStatus ||
    status === tracker.status ||
    status === ExpirationTrackerStatus.ACTIVE ||
    ((status === ExpirationTrackerStatus.RENEWED ||
      status === ExpirationTrackerStatus.CANCELLED) &&
      tracker.status !== ExpirationTrackerStatus.ACTIVE)

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <SectionHeader>Overview</SectionHeader>
        {onUpdateExpirationDate &&
          (editingDate ? (
            <div className="flex items-center gap-2">
              <button
                className="text-xs font-medium text-doubleNickel-success-600 hover:text-doubleNickel-success-700"
                onClick={handleSaveDate}
              >
                Save
              </button>
              <button
                className="text-xs text-doubleNickel-error-500 hover:text-doubleNickel-error-600"
                onClick={() => setEditingDate(false)}
              >
                Cancel
              </button>
            </div>
          ) : (
            <button
              className="flex items-center gap-1 text-xs text-doubleNickel-gray-400 hover:text-doubleNickel-brand-500"
              onClick={handleStartEdit}
            >
              <Pencil size={12} />
              Edit
            </button>
          ))}
      </div>

      {/* Status row with dropdown — matches VoE pattern */}
      <div className="flex items-center justify-between">
        <Text size="xs" fw={300}>
          Status
        </Text>
        <StyledMenu position="bottom-end">
          <Menu.Target>
            <div>
              <StyledBadge
                clickable
                textTransform="capitalize"
                color={ExpirationTrackerStatusColor[tracker.status] || 'gray'}
                rightSection={<ChevronDown size={12} />}
              >
                {formatSnakeValue(tracker.status)}
              </StyledBadge>
            </div>
          </Menu.Target>
          <Menu.Dropdown className="w-40">
            {STATUSES.map((status) => (
              <Menu.Item
                key={status}
                className="text-xs"
                disabled={isStatusDisabled(status)}
                onClick={() => onStatusChange(status)}
              >
                {formatSnakeValue(status)}
              </Menu.Item>
            ))}
          </Menu.Dropdown>
        </StyledMenu>
      </div>

      <DetailRow label="Applicant">
        {tracker.applicant ? (
          <button
            className="cursor-pointer text-doubleNickel-brand-500 hover:underline"
            onClick={(e) => {
              if (!onOpenApplicant) return
              onOpenApplicant(tracker.applicant.applicantId, e.metaKey)
            }}
          >
            {formatSnakeValue(tracker.applicant.firstName)}{' '}
            {formatSnakeValue(tracker.applicant.lastName)}
          </button>
        ) : (
          '—'
        )}
      </DetailRow>
      {tracker.applicant?.isCommunicationOptIn === false && (
        <div className="rounded-md border border-doubleNickel-warning-300 bg-doubleNickel-warning-50 px-3 py-2">
          <div className="flex items-center gap-1.5">
            <TriangleAlert size={12} className="shrink-0 stroke-doubleNickel-warning-600" />
            <Text size="xs" fw={500} className="text-doubleNickel-warning-700">
              Applicant has opted out of communications
            </Text>
          </div>
          <Text size="xs" className="mt-0.5 text-doubleNickel-warning-600">
            Applicant-facing SMS notifications will not be sent.
          </Text>
        </div>
      )}
      <DetailRow label="Category">
        {tracker.category
          ? DocumentCategoryLabel[tracker.category] ||
            formatSnakeValue(tracker.category)
          : '—'}
      </DetailRow>
      <DetailRow label="Expiration Date">
        {editingDate ? (
          <div className="flex flex-col items-end gap-1">
            <DateInput
              size="xs"
              value={editDate}
              onChange={setEditDate}
              valueFormat="MM/DD/YYYY"
              className="w-[130px]"
            />
            <DateShortcutButton
              onClick={(_event, date) => setEditDate(date)}
            />
          </div>
        ) : (
          <div className="flex items-center gap-1.5">
            <span
              className={onUpdateExpirationDate ? 'cursor-pointer hover:underline' : ''}
              onClick={onUpdateExpirationDate ? handleStartEdit : undefined}
            >
              {dayjs(tracker.expirationDate).format('MM/DD/YYYY')}
            </span>
            {expirationTooltip && (
              <Tooltip label={expirationTooltip} position="top">
                <CircleAlert size={12} className={alertIconClass} />
              </Tooltip>
            )}
          </div>
        )}
      </DetailRow>
      <DetailRow label="Days Remaining">
        <div className="flex items-center gap-1.5">
          <span>{daysLabel}</span>
          {expirationTooltip && (
            <Tooltip label={expirationTooltip} position="top">
              <CircleAlert size={12} className={alertIconClass} />
            </Tooltip>
          )}
        </div>
      </DetailRow>
    </div>
  )
}

export default OverviewSection
